import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAtomValue } from 'jotai';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getSDK } from '../services/sdk-instance';
import { holdsIdentityAtom } from '../stores/auth/atoms';
import { sdkConfiguredAtom } from '../stores/sdk/atoms';
import type { Message } from '../types/session-types';
import type { ChatSession } from '@localagent/sdk';

/**
 * The chat transcript binding (sdk ADR-377c6): a THIN binding over the ChatSession's transcript window.
 * The window state machine (tail page, older pages, live-tail catch-up,
 * rewind reset) and the history cache live in the SDK core; this hook only
 * subscribes, seeds TanStack, and calls through.
 *
 * The query key is unchanged from the `useMessages` read this replaces
 * (sdk ADR-4f834); the window's `loadTranscriptTail` is idempotent, so a
 * remount re-seeds from SDK state instead of refetching.
 */
export function useSessionTranscript(
  workspaceId: string | null,
  sessionId: string | undefined,
  chat: ChatSession | null,
) {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);
  const queryClient = useQueryClient();
  const queryKey = ['sessions', workspaceId, sessionId, 'messages'] as const;

  const query = useQuery<Message[], Error>({
    queryKey,
    queryFn: async () => {
      await chat!.loadTranscriptTail();
      return [...chat!.transcriptMessages];
    },
    enabled: Boolean(isAuthenticated && sdkConfigured && sessionId && chat),
  });

  useEffect(() => {
    if (!chat) return;
    return chat.onTranscriptWindow(() => {
      queryClient.setQueryData<Message[]>(['sessions', workspaceId, sessionId, 'messages'], [
        ...chat.transcriptMessages,
      ]);
    });
  }, [chat, queryClient, workspaceId, sessionId]);

  const [loadingOlder, setLoadingOlder] = useState(false);

  const loadOlder = useCallback(async () => {
    if (!chat || !sessionId) return;
    setLoadingOlder(true);
    try {
      // Both lanes share one sentinel — the two-lane load is one SDK
      // call each, fired in the same tick.
      await chat.loadOlderMessages();
      await chat.loadOlderEvents();
    } finally {
      setLoadingOlder(false);
    }
  }, [chat, sessionId]);

  return {
    ...query,
    loadOlder,
    loadingOlder,
    hasOlder: Boolean(chat && (chat.hasOlderMessages || chat.hasOlderEvents)),
  };
}

/**
 * Edit-and-rewind through the ChatSession wrapper: the SDK owns the
 * window reset + tail refetch, so the transcript stays correct without this
 * hook knowing the policy. Falls back to the raw resource only when no bound
 * handle exists (an edit needs a session; a draft cannot be here).
 */
export function useEditSessionMessage(
  workspaceId: string | null,
  sessionId: string | undefined,
  chat: ChatSession | null,
) {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const queryClient = useQueryClient();

  return useCallback(
    async (messageId: string, content: string) => {
      if (!sessionId) {
        throw new Error('Session required to edit a message');
      }
      if (chat && chat.sessionId === sessionId) {
        const result = await chat.editMessageAndRewind(messageId, content);
        if (isAuthenticated) {
          void queryClient.invalidateQueries({
            queryKey: ['sessions', workspaceId, sessionId, 'messages'],
          });
        }
        return result;
      }
      return getSDK().sessions.editMessageAndRewind(workspaceId, sessionId, messageId, content);
    },
    [chat, isAuthenticated, queryClient, sessionId, workspaceId],
  );
}

/** Max pages a deep-link jump loads on its own. */
export const DEEP_LINK_MAX_AUTO_PAGES = 5;

/**
 * The deep-link jump policy: for a
 * `#event=<id>` hash, highlight and scroll to the target, paging back through
 * `loadOlder` while the target sits below the loaded window — capped at
 * {@link DEEP_LINK_MAX_AUTO_PAGES} pages; past the cap `jumpNeedsMore` shows
 * and `armManualJump` continues the jump one explicit page at a time. A new
 * hash resets the cap.
 */
export function useDeepLinkMessageJump(params: {
  hash: string;
  messages: readonly { id: string }[] | undefined;
  hasOlder: boolean;
  loadOlder: () => Promise<unknown>;
  onTarget?: (eventId: string) => void;
}): { jumpNeedsMore: boolean; armManualJump: () => void } {
  const { hash, messages, hasOlder, loadOlder, onTarget } = params;
  const [pagesLoaded, setPagesLoaded] = useState(0);
  const [manualArmed, setManualArmed] = useState(false);
  const hashRef = useRef<string | null>(null);

  const targetId = useMemo(() => {
    const match = /^#event=(.+)$/.exec(hash);
    return match ? decodeURIComponent(match[1]) : null;
  }, [hash]);

  useEffect(() => {
    if (targetId === null) return;
    if (hashRef.current !== hash) {
      hashRef.current = hash;
      setPagesLoaded(0);
      setManualArmed(false);
    }
    if (!messages || messages.length === 0) return;
    onTarget?.(targetId);
    requestAnimationFrame(() => {
      const el = document.querySelector(`[data-message-id="${CSS.escape(targetId)}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      if (!hasOlder) return;
      if (pagesLoaded < DEEP_LINK_MAX_AUTO_PAGES || manualArmed) {
        void Promise.resolve(loadOlder()).then(() => {
          setPagesLoaded((c) => c + 1);
          setManualArmed(false);
        });
      }
    });
  }, [targetId, hash, messages, hasOlder, loadOlder, onTarget, pagesLoaded, manualArmed]);

  const targetLoaded = (messages ?? []).some((m) => m.id === targetId);
  const jumpNeedsMore =
    targetId !== null && !targetLoaded && hasOlder && pagesLoaded >= DEEP_LINK_MAX_AUTO_PAGES && !manualArmed;

  return { jumpNeedsMore, armManualJump: useCallback(() => setManualArmed(true), []) };
}
